import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import { useUser } from '../../hooks/useUser';
import type { Conversation } from '../../api/OasisBackendApi';

interface ConversationHistoryProps {
  onConversationSelect: (conversationId: number) => void;
  showSingleTurnConversations?: boolean;
  searchEnabled?: boolean;
}

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ 
  onConversationSelect,
  showSingleTurnConversations: initialShowSingleTurn = true,
  searchEnabled = false
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSingleTurn, setShowSingleTurn] = useState(initialShowSingleTurn);
  const db = useOasisBackend();
  const navigate = useNavigate();
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";

  useEffect(() => {
    const fetchConversations = async () => {
      if (!loggedIn) {
        setConversations([]);
        setIsLoading(false);
        return;
      }
    
      try {
        setIsLoading(true);
        const response = await db.endpoints.rhea.rheaApiConversationsList();
        // Sort conversations by creation date
        const sortedConversations = response.data.sort((a: Conversation, b: Conversation) => {
          const aTime = new Date(a.created_at).getTime();
          const bTime = new Date(b.created_at).getTime();
          return bTime - aTime;
        });
        setConversations(sortedConversations);
        setError(null);
      } catch (err) {
        setError('Failed to load conversations');
        console.error('Error loading conversations:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversations();
  }, [db.endpoints.rhea, loggedIn]);

  // Filter conversations based on search query and single-turn filter
  useEffect(() => {
    let filtered = [...conversations];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv => 
        (conv.title || '').toLowerCase().includes(query) ||
        (conv.messages?.[0]?.content || '').toLowerCase().includes(query)
      );
    }
    
    // Apply single-turn filter
    if (!showSingleTurn) {
      filtered = filtered.filter(conv => 
        !conv.messages || conv.messages.length > 2
      );
    }
    
    setFilteredConversations(filtered);
  }, [conversations, searchQuery, showSingleTurn]);

  if (isLoading) {
    return (
      <div className="p-4 text-center" style={{ color: 'var(--rhea-text-secondary)' }}>
        Loading conversations...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center" style={{ color: 'var(--rhea-text-error)' }}>
        {error}
      </div>
    );
  }



  return (
    <div className="h-full flex flex-col">
      {/* Search and Filter Controls */}
      {loggedIn && (
      <div className="p-4 border-b" style={{ borderColor: 'var(--rhea-card-border)' }}>
        {searchEnabled && (
          <div className="mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full px-3 py-1.5 text-sm rounded-full border bg-[var(--rhea-bg-primary)] outline-none"
              style={{ 
                borderColor: 'var(--rhea-card-border)',
                color: 'var(--rhea-text-primary)'
              }}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="text-sm" style={{ color: 'var(--rhea-text-secondary)' }}>
            <input
              type="checkbox"
              checked={!showSingleTurn}
              onChange={(e) => setShowSingleTurn(!e.target.checked)}
              className="mr-2"
            />
            Hide single-turn conversations
          </label>
          </div>
        </div>
      )}
      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {!loggedIn ? (
          <div className="h-full flex items-center justify-center p-4 text-center" style={{ color: 'var(--rhea-text-secondary)' }}>
            <em>Login to enable conversation history</em>
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => onConversationSelect(conversation.id)}
              className="px-4 py-2 m-2 rounded border-b cursor-pointer hover:bg-[var(--rhea-card-hover)] transition-colors"
              style={{ borderColor: 'var(--rhea-card-border)' }}
            >
              <div className="flex flex-col gap-1">
                <div className="font-medium" style={{ color: 'var(--rhea-text-primary)' }}>
                  {conversation.title || conversation.messages[0]?.content?.slice(0, 50)}
                </div>
                {conversation.messages && conversation.messages.length > 0 && (
                  <div className="text-m truncate italic" style={{ color: 'var(--rhea-text-secondary)' }}>
                    {conversation.messages[0].content}
                  </div>
                )}
                <div className="text-xs opacity-50" style={{ color: 'var(--rhea-text-secondary)' }}>
                  {new Date(conversation.created_at).toLocaleString()}
                </div>
                
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationHistory; 